import {
  Box,
  type ChipProps,
  Chip as MuiChip,
  Stack,
  type SxProps,
  type Theme,
  inputBaseClasses,
  styled,
} from "@mui/material"
import { useMemo, useRef, useState } from "react"
import { ChevronDown } from "react-feather"
import { FilterFieldTextField } from "../FilterField"
import { type TreeNode, TreeSelectMenu } from "../TreeSelectMenu"
import { flattenTree } from "./TreeSelectFields.utils"

export interface TreeSelectFieldProps<T extends string = string> {
  options: TreeNode<T>[]
  value: T[]
  /** Fired with the final selection, batched on menu close */
  onChange: (value: T[]) => void
  placeholder?: string
  searchPlaceholder?: string
  sx?: SxProps<Theme>
  chipsToDisplay?: number
  slotProps?: SlotProps
}

interface SlotProps {
  chip?: Omit<ChipProps, "label" | "color">
}

const MAX_CHIPS_TO_DISPLAY = 1
const CHEVRON_SIZE = 18
const SPACING = 0.5

export const TreeSelectField = <T extends string = string>({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  sx,
  chipsToDisplay = MAX_CHIPS_TO_DISPLAY,
  slotProps,
}: TreeSelectFieldProps<T>) => {
  const [open, setOpen] = useState(false)
  const [draftValue, setDraftValue] = useState<T[]>(value)
  const anchorRef = useRef<HTMLDivElement>(null)

  const displayedValue = open ? draftValue : value

  const flatOptions = useMemo(() => flattenTree(options), [options])
  const selectedLabels = flatOptions
    .filter((option) => displayedValue.includes(option.code))
    .map((option) => option.label)
  const isEmpty = selectedLabels.length === 0

  const openMenu = () => {
    setDraftValue(value)
    setOpen(true)
  }

  const handleChange = (next: T[]) => {
    setDraftValue(next)
  }

  const handleClose = () => {
    setOpen(false)
    onChange([...draftValue].sort())
  }

  return (
    <>
      <RoundedTextField
        ref={anchorRef}
        onClick={openMenu}
        size="small"
        isEmpty={isEmpty}
        sx={sx}
        slotProps={{
          htmlInput: {
            "aria-label": placeholder,
            role: "button",
            "aria-haspopup": "menu",
            "aria-expanded": open,
          },
          input: {
            readOnly: true,
            startAdornment: isEmpty ? (
              /** Rendered as an adornment instead of the native input placeholder:
               * Old browser doesn't support `field-sizing: content`
               **/
              !!placeholder && <Placeholder>{placeholder}</Placeholder>
            ) : (
              <ChipsContainer>
                <ChipsRow>
                  {selectedLabels.slice(0, chipsToDisplay).map((option) => (
                    <Chip label={option} key={option} {...slotProps?.chip} />
                  ))}
                </ChipsRow>
                {selectedLabels.length > chipsToDisplay && (
                  <CounterChip
                    label={`+${selectedLabels.length - chipsToDisplay}`}
                    {...slotProps?.chip}
                  />
                )}
              </ChipsContainer>
            ),
            endAdornment: <Chevron open={open} />,
          },
        }}
      />
      <TreeSelectMenu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        options={options}
        value={draftValue}
        searchPlaceholder={searchPlaceholder}
        onChange={handleChange}
      />
    </>
  )
}

const Chevron = ({ open }: { open: boolean }) => (
  <Box
    width={CHEVRON_SIZE}
    height={CHEVRON_SIZE}
    color={(t) => t.palette.common.darkBlue}
  >
    <ChevronDown
      size={CHEVRON_SIZE}
      style={{
        transition: "transform 0.2s",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  </Box>
)

const Placeholder = styled("span")({
  whiteSpace: "nowrap",
})

const Row = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(SPACING),
}))

const ChipsContainer = styled(Row)(({ theme }) => ({
  width: `calc(100% - calc(${CHEVRON_SIZE}px + ${theme.spacing(SPACING)}))`,
}))
const ChipsRow = styled(Row)({
  overflow: "hidden",
})

const Chip = styled(MuiChip)(({ theme: { palette, typography } }) => ({
  backgroundColor: palette.common.lightBlue,
  minWidth: 0,
  fontWeight: 500,
  fontSize: typography.pxToRem(14),
}))

const CounterChip = styled(Chip)({
  flexShrink: 0,
})

const RoundedTextField = styled(FilterFieldTextField, {
  shouldForwardProp: (props) => props !== "isEmpty",
})<{ isEmpty: boolean }>(({ isEmpty, theme }) => ({
  width: "min-content",
  [`& .${inputBaseClasses.root}`]: {
    paddingLeft: isEmpty ? theme.spacing(1.75) : theme.spacing(0.6),
    maxWidth: "inherit",
    minWidth: "inherit",
  },
  [`& .${inputBaseClasses.input}`]: {
    marginRight: isEmpty ? theme.spacing(1.5) : SPACING,
    paddingBlock: 0,
  },
}))
